import { Curve3, GroundMesh, Mesh, SubMesh, Vector3, VertexData } from '@babylonjs/core';
import { GameConfig } from './GameConfig';
import { Level, PathInfo } from './Level';
import { PathConfig, PatternConfig } from './LevelConfig';

/** Shape array that may include solid-shape metadata for manifold mesh generation */
export type SolidShape = Vector3[] & { solidHalfCount?: number; closedShape?: boolean };

export type PatternType =
  | 'halfCircle'
  | 'halfCircleDepth'
  | 'circle'
  | 'ellipse'
  | 'horizontalLine'
  | 'verticalLine'
  | 'rectangle'
  | 'star'
  | 'depthLine'
  | 'diagonalUp'
  | 'diagonalDown'
  | 'plane';

export interface PatternTypeConfig {
  type: PatternType;
  scale?: number;
  segments?: number;
  width?: number;
  height?: number;
  depth?: number;
  thickness?: number;
}

export class PathFactory {
  static getPatternPositions(
    config: PatternConfig,
    position: Vector3,
    laneWidth: number,
    pathInfo: PathInfo,
    alignToPath: boolean = true
  ): Vector3[] {
    const createdPositions: Vector3[] = [];
    const basePatternPosition = position.clone();

    if (config.preset) {
      const positions = PathFactory.createPattern(config.preset);
      for (const pattern of positions) {
        if (alignToPath) {
          const patternPosition = basePatternPosition.clone();
          // Align X to path's lateral direction, keep Y as is
          patternPosition.addInPlace(pathInfo.lateral.scale(pattern.x));
          patternPosition.y += pattern.y;
          patternPosition.z += pattern.z;
          createdPositions.push(patternPosition);
        } else {
          createdPositions.push(basePatternPosition.add(pattern));
        }
      }
    }

    if (config.positions) {
      for (const pattern of config.positions) {
        if (alignToPath) {
          const patternPosition = basePatternPosition.clone();
          // Align X to path's lateral direction, keep Y as is
          patternPosition.addInPlace(pathInfo.lateral.scale(pattern.x));
          patternPosition.y += pattern.y;
          patternPosition.z += pattern.z;
          createdPositions.push(patternPosition);
        } else {
          createdPositions.push(basePatternPosition.add(pattern));
        }
      }
    }

    if (config.lanes) {
      for (const pattern of config.lanes) {
        const patternPosition = basePatternPosition.clone();
        patternPosition.addInPlace(pathInfo.lateral.scale(pattern * laneWidth));
        createdPositions.push(patternPosition);
      }
    }

    return createdPositions;
  }

  static createPattern(config: PatternTypeConfig): Vector3[] {
    let points: Vector3[] = [];
    switch (config.type) {
      case 'halfCircle':
        points = PathFactory.shapeHalfCircle(config.segments, config.width / 2);
        break;
      case 'halfCircleDepth':
        points = PathFactory.shapeHalfCircleDepth(config.segments, config.depth / 2);
        break;
      case 'circle':
        points = PathFactory.shapeCircle(config.segments, config.width / 2);
        break;
      case 'ellipse':
        points = PathFactory.shapeEllipse(config.segments, config.width, config.height);
        break;
      case 'horizontalLine':
        points = PathFactory.shapeHorizontalLine(config.width, config.segments);
        break;
      case 'depthLine':
        points = PathFactory.shapeDepthLine(config.segments, config.depth);
        break;
      case 'diagonalUp':
        points = PathFactory.shapeDiagonalUp(config.segments, config.width, config.height);
        break;
      case 'diagonalDown':
        points = PathFactory.shapeDiagonalDown(config.segments, config.width, config.height);
        break;
      case 'verticalLine':
        points = PathFactory.shapeVerticalLine(config.height, config.segments);
        break;
      case 'rectangle':
        points = PathFactory.shapeRectangle(config.width, config.height);
        break;
      case 'star':
        points = PathFactory.shapeStar(config.segments, config.width);
        break;
      case 'plane':
        points = PathFactory.shapePlane(config.segments, config.width, config.height);
        break;
    }

    return PathFactory.scalePath(points, config.scale || 1);
  }

  static shapeRectangle(width = 1, height = 0.25, thickness = 0): SolidShape {
    const points: SolidShape = [
      new Vector3(-width, 0, 0),
      new Vector3(width, 0, 0),
      new Vector3(width, -height, 0),
      new Vector3(-width, -height, 0),
    ];
    if (thickness > 0) {
      const iw = width - thickness;
      const ih = height - thickness;
      // Inner rectangle (reversed winding)
      points.push(
        new Vector3(-iw, -thickness, 0),
        new Vector3(iw, -thickness, 0),
        new Vector3(iw, -(thickness + ih), 0),
        new Vector3(-iw, -(thickness + ih), 0)
      );
      // Reverse inner ring for correct winding
      const inner = points.splice(4, 4).reverse();
      points.push(...inner);
      points.solidHalfCount = 4;
    }
    return points;
  }
  /**
   * Creates a U-Profile shape (channel/gutter)
   * @param width Half-width of the U (total width = 2 * width)
   * @param height Height of the walls
   * @param thickness Wall thickness
   */
  static shapeUProfile(width = 1, height = 0.5, thickness = 0.25): SolidShape {
    const innerWidth = width - thickness;
    const points: SolidShape = [
      // outer side (clockwise)
      new Vector3(-width, height, 0),
      new Vector3(-width, 0, 0),
      new Vector3(width, 0, 0),
      new Vector3(width, height, 0),
      // inner side (counter-clockwise)
      new Vector3(innerWidth, height, 0),
      new Vector3(innerWidth, thickness, 0),
      new Vector3(-innerWidth, thickness, 0),
      new Vector3(-innerWidth, height, 0),
    ];
    points.solidHalfCount = 4;
    points.closedShape = false;
    return points;
  }

  /**
   * Creates a triangle shape
   * @param width Half-width of the base (total width = 2 * width)
   * @param height Height of the triangle
   * @param thickness Optional wall thickness for solid shape
   */
  static shapeTriangle(width = 1, height = 1, thickness = 0): SolidShape {
    const points: SolidShape = [new Vector3(0, height, 0), new Vector3(width, 0, 0), new Vector3(-width, 0, 0)];
    if (thickness > 0) {
      // Inset triangle
      const scale = Math.max(0.01, 1 - thickness / Math.min(width, height));
      points.push(
        new Vector3(0, height * scale + thickness * 0.5, 0),
        new Vector3(width * scale, thickness, 0),
        new Vector3(-width * scale, thickness, 0)
      );
      // Reverse inner ring for correct winding
      const inner = points.splice(3, 3).reverse();
      points.push(...inner);
      points.solidHalfCount = 3;
    }
    return points;
  }

  /**
   * Creates a pipe (round pipe)
   * @param segments Segment count
   * @param radius Radius of the pipe
   * @param thickness Optional wall thickness. When provided, creates a solid annular
   *   cross-section (outer + inner ring) suitable for CSG2 boolean operations.
   *   The returned shape has 2*segments points: outer ring then reversed inner ring.
   * @param excludeBottomPoints Number of points to exclude from the bottom of the pipe.
   * @returns Array of Vector3 points
   */
  static shapePipe(segments = 16, radius = 1, thickness = 0, excludeBottomPoints: number = 0): Vector3[] {
    const points: SolidShape = [];

    // Determine which indices to exclude (bottom-most points of the circle).
    // The bottom of the circle is at angle 3π/2 (index = segments * 3/4).
    const bottomCenter = Math.round((segments * 3) / 4);
    const excludeStart = bottomCenter - Math.floor(excludeBottomPoints / 2);
    const excludeSet = new Set<number>();
    for (let e = 0; e < excludeBottomPoints; e++) {
      excludeSet.add((((excludeStart + e) % segments) + segments) % segments);
    }

    // Outer ring – skip excluded bottom points, insert flat segment instead
    let flatBottomY: number | null = null;
    let insertedFlat = false;
    for (let i = 0; i < segments; i++) {
      if (excludeSet.has(i)) {
        // On first excluded point, record the y of the previous (last kept) point
        // and insert two flat-bottom bridge points
        if (!insertedFlat && points.length > 0) {
          flatBottomY = points[points.length - 1].y;
          insertedFlat = true;
        }
        continue;
      }
      // After the excluded gap, add a flat-bottom bridge point before continuing the arc
      if (insertedFlat && flatBottomY !== null) {
        const angle = (i / segments) * Math.PI * 2;
        const x = Math.cos(angle) * radius;
        points.push(new Vector3(x, flatBottomY, 0));
        insertedFlat = false; // only once
      }
      const angle = (i / segments) * Math.PI * 2;
      points.push(new Vector3(Math.cos(angle) * radius, Math.sin(angle) * radius, 0));
    }

    // If thickness is specified, add the inner ring in reverse order
    // This creates a closed annular cross-section for solid mesh extrusion
    if (thickness > 0) {
      const innerRadius = Math.max(0.01, radius - thickness);
      const innerPoints: Vector3[] = [];

      let innerFlatBottomY: number | null = null;
      let innerInsertedFlat = false;
      for (let i = segments - 1; i >= 0; i--) {
        if (excludeSet.has(i)) {
          if (!innerInsertedFlat && innerPoints.length > 0) {
            innerFlatBottomY = innerPoints[innerPoints.length - 1].y;
            innerInsertedFlat = true;
          }
          continue;
        }
        if (innerInsertedFlat && innerFlatBottomY !== null) {
          const angle = (i / segments) * Math.PI * 2;
          const x = Math.cos(angle) * innerRadius;
          innerPoints.push(new Vector3(x, innerFlatBottomY, 0));
          innerInsertedFlat = false;
        }
        const angle = (i / segments) * Math.PI * 2;
        innerPoints.push(new Vector3(Math.cos(angle) * innerRadius, Math.sin(angle) * innerRadius, 0));
      }

      points.push(...innerPoints);
      points.solidHalfCount = points.length - innerPoints.length;
      points.closedShape = true;
    }
    return points;
  }

  /**
   * Creates a flat pipe (oval pipe)
   * @param segments Segment count
   * @param width
   * @param height
   * @param thickness Optional wall thickness for solid shape
   * @returns Array of Vector3 points
   */
  static shapeFlatPipe(segments = 16, width = 1, height = 0.25, thickness = 0): SolidShape {
    const points: SolidShape = [];
    for (let i = 0; i < segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      points.push(new Vector3(Math.cos(angle) * width, Math.sin(angle) * height, 0));
    }
    if (thickness > 0) {
      const iw = Math.max(0.01, width - thickness);
      const ih = Math.max(0.01, height - thickness);
      for (let i = segments - 1; i >= 0; i--) {
        const angle = (i / segments) * Math.PI * 2;
        points.push(new Vector3(Math.cos(angle) * iw, Math.sin(angle) * ih, 0));
      }
      points.solidHalfCount = segments;
    }
    return points;
  }

  /**
   * Creates a circle shape (flat disc cross-section)
   * @param segments Segment count for circle approximation
   * @param radius Radius of the circle
   * @param thickness Optional wall thickness for solid shape
   * @returns Array of Vector3 points forming a circle
   */
  static shapeCircle(segments = 16, radius = 1, thickness = 0): SolidShape {
    const points: SolidShape = [];
    for (let i = 0; i < segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      points.push(new Vector3(Math.cos(angle) * radius, Math.sin(angle) * radius, 0));
    }
    if (thickness > 0) {
      const ir = Math.max(0.01, radius - thickness);
      for (let i = segments - 1; i >= 0; i--) {
        const angle = (i / segments) * Math.PI * 2;
        points.push(new Vector3(Math.cos(angle) * ir, Math.sin(angle) * ir, 0));
      }
      points.solidHalfCount = segments;
    }
    return points;
  }

  /**
   * Creates an ellipse shape
   * @param segments Segment count for ellipse approximation
   * @param width Half-width of the ellipse (X radius)
   * @param height Half-height of the ellipse (Y radius)
   * @param thickness Optional wall thickness for solid shape
   * @returns Array of Vector3 points forming an ellipse
   */
  static shapeEllipse(segments = 16, width = 1, height = 0.5, thickness = 0): SolidShape {
    const points: SolidShape = [];
    for (let i = 0; i < segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      points.push(new Vector3(Math.cos(angle) * width, Math.sin(angle) * height, 0));
    }
    if (thickness > 0) {
      const iw = Math.max(0.01, width - thickness);
      const ih = Math.max(0.01, height - thickness);
      for (let i = segments - 1; i >= 0; i--) {
        const angle = (i / segments) * Math.PI * 2;
        points.push(new Vector3(Math.cos(angle) * iw, Math.sin(angle) * ih, 0));
      }
      points.solidHalfCount = segments;
    }
    return points;
  }

  /**
   * Creates a horizontal line shape centered at 0,0,0
   * @param width Total width of the line
   * @param segments Number of points along the line
   * @returns Array of Vector3 points (middle segment at 0,0,0, left positions are negative)
   */
  static shapeHorizontalLine(width = 2, segments = 5): Vector3[] {
    const points: Vector3[] = [];
    const halfWidth = width / 2;
    for (let i = 0; i < segments; i++) {
      const t = segments > 1 ? i / (segments - 1) : 0.5;
      const x = -halfWidth + t * width;
      points.push(new Vector3(x, 0, 0));
    }
    return points;
  }

  /**
   * Creates a vertical line shape with 0,0,0 at the bottom
   * @param height Total height of the line
   * @param segments Number of points along the line
   * @returns Array of Vector3 points (bottom at 0,0,0, going upward)
   */
  static shapeVerticalLine(height = 2, segments = 5): Vector3[] {
    const points: Vector3[] = [];
    for (let i = 0; i < segments; i++) {
      const t = segments > 1 ? i / (segments - 1) : 0;
      const y = t * height;
      points.push(new Vector3(0, y, 0));
    }
    return points;
  }

  /**
   * Creates a half circle (upper arc)
   * @param segments Number of segments for the arc
   * @param radius Radius of the half circle
   * @returns Array of Vector3 points forming the upper half of a circle
   */
  static shapeHalfCircle(segments = 8, radius = 1): Vector3[] {
    const points: Vector3[] = [];
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * Math.PI; // 0 to PI (upper half)
      points.push(new Vector3(Math.cos(angle) * radius, Math.sin(angle) * radius, 0));
    }
    return points;
  }

  /**
   * Creates a half circle along the Z-axis (depth)
   * @param segments Number of segments for the arc
   * @param radius Radius of the half circle
   * @returns Array of Vector3 points forming an arc along Z, 0,0,0 at bottom-front
   */
  static shapeHalfCircleDepth(segments = 8, radius = 1): Vector3[] {
    const points: Vector3[] = [];
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * Math.PI; // 0 to PI
      const y = Math.sin(angle) * radius; // Height
      const z = (1 - Math.cos(angle)) * radius; // Depth, starting at 0
      points.push(new Vector3(0, y, z));
    }
    return points;
  }

  /**
   * Creates a star shape
   * @param points Number of star points (5 = 5-pointed star)
   * @param outerRadius Outer radius of the star
   * @param innerRadius Inner radius of the star (default: outerRadius * 0.4)
   * @returns Array of Vector3 points forming a star
   */
  static shapeStar(points = 5, outerRadius = 1, innerRadius?: number): Vector3[] {
    const result: Vector3[] = [];
    const inner = innerRadius ?? outerRadius * 0.4;
    const totalPoints = points * 2;
    for (let i = 0; i < totalPoints; i++) {
      const angle = (i / totalPoints) * Math.PI * 2 - Math.PI / 2; // Start at top
      const radius = i % 2 === 0 ? outerRadius : inner;
      result.push(new Vector3(Math.cos(angle) * radius, Math.sin(angle) * radius, 0));
    }
    return result;
  }

  /**
   * Creates a depth line shape along the Z-axis
   * @param segments Number of points along the line
   * @param depth Total depth of the line
   * @returns Array of Vector3 points along Z-axis
   */
  static shapeDepthLine(segments = 5, depth = 2): Vector3[] {
    const points: Vector3[] = [];
    const halfDepth = depth / 2;
    for (let i = 0; i < segments; i++) {
      const t = segments > 1 ? i / (segments - 1) : 0.5;
      const z = -halfDepth + t * depth;
      points.push(new Vector3(0, 0, z));
    }
    return points;
  }

  /**
   * Creates a diagonal line going right and up
   * @param segments Number of points along the line
   * @param width Total width (X distance)
   * @param height Total height (Y distance)
   * @returns Array of Vector3 points (0,0,0 is the bottom-left point)
   */
  static shapeDiagonalUp(segments = 5, width = 2, height = 2): Vector3[] {
    const points: Vector3[] = [];
    for (let i = 0; i < segments; i++) {
      const t = segments > 1 ? i / (segments - 1) : 0;
      const x = t * width;
      const y = t * height;
      points.push(new Vector3(x, y, 0));
    }
    return points;
  }

  /**
   * Creates a diagonal line going right and down
   * @param segments Number of points along the line
   * @param width Total width (X distance)
   * @param height Total height (Y distance)
   * @returns Array of Vector3 points (0,0,0 is the top-left point)
   */
  static shapeDiagonalDown(segments = 5, width = 2, height = 2): Vector3[] {
    const points: Vector3[] = [];
    for (let i = 0; i < segments; i++) {
      const t = segments > 1 ? i / (segments - 1) : 0;
      const x = t * width;
      const y = -t * height;
      points.push(new Vector3(x, y, 0));
    }
    return points;
  }

  /**
   * Creates a plane (grid) of positions
   * @param segments Number of points per row
   * @param width Total width of the plane
   * @param height Total height of the plane
   * @returns Array of Vector3 points forming a grid
   */
  static shapePlane(segments = 3, width = 2, height = 2): Vector3[] {
    const points: Vector3[] = [];
    const halfWidth = width / 2;
    const halfHeight = height / 2;
    for (let row = 0; row < segments; row++) {
      const ty = segments > 1 ? row / (segments - 1) : 0.5;
      const y = -halfHeight + ty * height;
      for (let col = 0; col < segments; col++) {
        const tx = segments > 1 ? col / (segments - 1) : 0.5;
        const x = -halfWidth + tx * width;
        points.push(new Vector3(x, y, 0));
      }
    }
    return points;
  }

  /**
   * Scales a path
   * @param path Path to scale
   * @param scale Scale factor
   * @returns Scaled path
   */
  static scalePath(path: Vector3[], scale: number): Vector3[] {
    const points: Vector3[] = [];
    for (let i = 0; i < path.length; i++) {
      const p = path[i];
      points.push(p.scale(scale));
    }
    return points;
  }

  /**
   * Rasterizes a path to a specific number of segments
   * @param path Path to rasterize
   * @param segments Number of segments
   * @returns Rasterized path
   */
  static rasterizePath(path: Vector3[], segments: number): Vector3[] {
    const points: Vector3[] = [];
    for (let i = 0; i < segments; i++) {
      const t = i / segments;
      const p = path[Math.floor(t * path.length)];
      points.push(p);
    }
    return points;
  }

  /**
   * Rasterizes a path to a specific segment length
   *
   * @param path Path to rasterize
   * @param length Length of each segment
   * @returns Rasterized path
   */
  static rasterizePathToSegmentLength(path: Vector3[], length: number): Vector3[] {
    if (path.length === 0) return [];
    const points: Vector3[] = [path[0]];
    let currentLength = 0;
    for (let i = 1; i < path.length; i++) {
      const p = path[i];
      const prevP = path[i - 1];
      currentLength += Vector3.Distance(prevP, p);
      if (currentLength >= length) {
        points.push(p);
        currentLength = 0;
      }
    }
    return points;
  }

  /**
   * Creates a halfpipe with thickness
   * @param width Width of the U
   * @param height Height of the walls
   * @param depth Depth of the U-bottom
   * @param thickness Wall thickness
   * @returns Array of Vector3 points
   */
  static shapeHalfpipe(width = 1, height = 1 / 6, depth = 1 / 6, thickness = 0.5 / 6): SolidShape {
    const points: SolidShape = [
      // outer side (clockwise)
      new Vector3(-width, height, 0), // left top outer
      new Vector3(-width, 0, 0), // left bottom outer
      new Vector3(0, -depth, 0), // center bottom outer
      new Vector3(width, 0, 0), // right bottom outer
      new Vector3(width, height, 0), // right top outer
      // inner side (counter-clockwise)
      new Vector3(width - thickness, height, 0), // right top inner
      new Vector3(width - thickness, thickness, 0), // right bottom inner
      new Vector3(0, -depth + thickness, 0), // center bottom inner
      new Vector3(-width + thickness, thickness, 0), // left bottom inner
      new Vector3(-width + thickness, height, 0), // left top inner
    ];
    points.solidHalfCount = 5; // 5 outer + 5 inner points
    points.closedShape = false;
    return points;
  }

  constructor(public level: Level) {}

  /**
   * Creates a smooth, curved path from start to destination with random variations
   * @returns Array of Vector3 points with equal segment lengths
   */
  createLevelPath(config: PathConfig): Vector3[] {
    // 1. Generate control points along the line from start to destination
    const controlPoints: Vector3[] = [config.start];
    const direction = config.destination.subtract(config.start);
    const totalDistance = direction.length();
    const stepDistance = totalDistance / (config.pathPoints + 1);

    // Calculate perpendicular vectors for random offsets
    const forward = direction.normalize();
    const right = Vector3.Cross(Vector3.Up(), forward).normalize();
    const up = Vector3.Cross(forward, right).normalize();

    // Maximum offset as percentage of step distance, scaled by pathCurvature (0=straight, 1=max winding)
    const curvature = config.pathCurvature;
    const maxLateralOffset = stepDistance * 0.8 * curvature;

    // Hilliness affects vertical offsets
    const hilliness = config.pathHilliness;
    const maxVerticalOffset = stepDistance * 0.5 * hilliness;

    // Use seeds for consistent noise
    const seedX = this.level.floatInRange(0, 1) * 1000;
    const seedY = this.level.floatInRange(0, 1) * 1000;

    // Scale controls the frequency of the noise.
    // Higher scale = lower frequency = longer curves.
    const noiseFrequency = 10 / Math.max(0.1, config.pathScale);

    const minDotProduct = Math.cos((180 - config.pathMinCurveAngle) * (Math.PI / 180));

    for (let i = 1; i <= config.pathPoints; i++) {
      // Base position along the line
      const t = i / (config.pathPoints + 1);
      const basePos = Vector3.Lerp(config.start, config.destination, t);

      let lateralOffset = 0;
      let verticalOffset = 0;
      let offsetPos = Vector3.Zero();
      let isValid = false;
      let attempts = 0;

      // Try to find a valid point that satisfies the angle constraint
      while (!isValid && attempts < 10) {
        // Reduce magnitude with attempts to converge to straight line
        const dampening = 1 - attempts / 10;

        // Use noise for offsets instead of random
        // We use 'attempts' as an offset in the noise space to get different values if we need to retry
        // t * noiseFrequency gives us coherent noise along the path
        const noiseInput = t * noiseFrequency;
        const attemptOffset = attempts * 100;

        // Simplex noise returns -1 to 1.
        // We use different seeds for X and Y/up.
        const noiseX = this.level.noise.simplex2(noiseInput, seedX + attemptOffset);
        const noiseY = this.level.noise.simplex2(noiseInput, seedY + attemptOffset);

        lateralOffset = noiseX * maxLateralOffset * dampening;
        verticalOffset = noiseY * maxVerticalOffset * dampening;

        offsetPos = basePos.add(right.scale(lateralOffset)).add(up.scale(verticalOffset));

        // Check angle constraint with previous segment
        if (i > 0) {
          const prevPoint = controlPoints[i - 1];
          const prevPrevPoint = i > 1 ? controlPoints[i - 2] : config.start.subtract(forward);

          const v1 = prevPoint.subtract(prevPrevPoint).normalize();
          const v2 = offsetPos.subtract(prevPoint).normalize();

          const dot = Vector3.Dot(v1, v2);

          if (dot >= minDotProduct) {
            isValid = true;
          }
        } else {
          isValid = true;
        }

        attempts++;
      }

      controlPoints.push(offsetPos);
    }
    controlPoints.push(config.destination);

    // 2. Create smooth curve through control points
    const curve = Curve3.CreateCatmullRomSpline(controlPoints, config.segmentsPerCurve, false);
    const rawPoints = curve.getPoints();

    // 3. Resample path to equal segment lengths
    const resampledPoints = this.resamplePathToEqualSegments(rawPoints, rawPoints.length);

    if (GameConfig.logDebug) {
      console.log(`[PathFactory] Created path with ${resampledPoints.length} points`);
    }

    return resampledPoints;
  }

  /**
   * Resamples a path to have equal-length segments
   */
  private resamplePathToEqualSegments(points: Vector3[], numSegments: number): Vector3[] {
    if (points.length < 2) return points;

    // Calculate total path length
    let totalLength = 0;
    const segmentLengths: number[] = [];
    for (let i = 1; i < points.length; i++) {
      const len = Vector3.Distance(points[i - 1], points[i]);
      segmentLengths.push(len);
      totalLength += len;
    }

    const targetSegmentLength = totalLength / numSegments;
    const resampled: Vector3[] = [points[0].clone()];

    let pointIndex = 0;
    let accumulatedDistance = 0;

    for (let i = 1; i < numSegments; i++) {
      const targetDistance = i * targetSegmentLength;

      // Move along the path until we reach targetDistance
      while (pointIndex < segmentLengths.length && accumulatedDistance + segmentLengths[pointIndex] < targetDistance) {
        accumulatedDistance += segmentLengths[pointIndex];
        pointIndex++;
      }

      if (pointIndex >= segmentLengths.length) {
        resampled.push(points[points.length - 1].clone());
        continue;
      }

      // Interpolate within current segment
      const remainingDistance = targetDistance - accumulatedDistance;
      const t = remainingDistance / segmentLengths[pointIndex];
      const interpolated = Vector3.Lerp(points[pointIndex], points[pointIndex + 1], t);
      resampled.push(interpolated);
    }

    resampled.push(points[points.length - 1].clone());
    return resampled;
  }

  generateValleyPathToGoal(
    ground: GroundMesh,
    start: Vector3,
    goal: Vector3,
    steps: number = 100,
    stepSize: number = 2,
    angleSpread: number = Math.PI / 8
  ): Vector3[] {
    const path: Vector3[] = [start.clone()];
    let current = start.clone();

    for (let i = 0; i < steps; i++) {
      const toGoal = goal.subtract(current).normalize();
      const baseAngle = Math.atan2(toGoal.z, toGoal.x);

      const candidates: { pos: Vector3; height: number; direction: Vector3 }[] = [];

      for (let j = 0; j < 10; j++) {
        const angle = baseAngle + (Math.random() - 0.5) * angleSpread;
        const dir = new Vector3(Math.cos(angle), 0, Math.sin(angle));
        const candidatePos = current.add(dir.scale(stepSize));
        const height = ground.getHeightAtCoordinates(candidatePos.x, candidatePos.z);
        if (height === undefined || isNaN(height)) continue;

        const worldPos = new Vector3(candidatePos.x, height, candidatePos.z);
        candidates.push({ pos: worldPos, height, direction: dir });
      }

      // Sort by height ascending (prefer valleys), but slightly bias toward direction to goal
      candidates.sort((a, b) => {
        const biasA = Vector3.Dot(a.direction, toGoal);
        const biasB = Vector3.Dot(b.direction, toGoal);
        return a.height - b.height - 0.1 * (biasB - biasA); // prioritize low and forward
      });

      const best = candidates[0];
      if (!best) break;

      current = best.pos;
      path.push(current);

      // Stop if we get close to the goal
      if (Vector3.Distance(current, goal) < stepSize * 1.5) break;
    }

    return path;
  }

  generateValleyPath(
    ground: GroundMesh,
    start: Vector3,
    segmentCount: number = 100,
    segmentLength: number = 2,
    avoidanceRadius: number = 2,
    valleyBiasStrength: number = 0.9,
    maxTurnAngle: number = Math.PI / 8
  ): Vector3[] {
    const path: Vector3[] = [start.clone()];
    const visited: Vector3[] = [];

    let currentAngle = Math.random() * Math.PI * 2;

    for (let i = 0; i < segmentCount; i++) {
      const currentPos = path[path.length - 1];

      // Generate candidate directions
      const candidates: Vector3[] = [];
      const steps = 8;
      for (let j = -steps; j <= steps; j++) {
        const angle = currentAngle + j * (maxTurnAngle / steps);
        const dx = Math.cos(angle) * segmentLength;
        const dz = Math.sin(angle) * segmentLength;
        const nx = currentPos.x + dx;
        const nz = currentPos.z + dz;
        const ny = ground.getHeightAtCoordinates(nx, nz);

        if (ny === null) continue;

        candidates.push(new Vector3(nx, ny, nz));
      }

      // Score candidates by height and crossing risk
      let bestCandidate: Vector3 | null = null;
      let bestScore = Infinity;

      for (const cand of candidates) {
        // Skip if too close to any previous point (avoid crossings)
        let tooClose = visited.some((v) => Vector3.Distance(v, cand) < avoidanceRadius);
        if (tooClose) continue;

        // Lower score = better; we prefer low elevation
        const heightScore = cand.y;
        const distPenalty = Vector3.Distance(currentPos, cand); // should be ~segmentLength
        const score = valleyBiasStrength * heightScore + (1 - valleyBiasStrength) * distPenalty;

        if (score < bestScore) {
          bestScore = score;
          bestCandidate = cand;
        }
      }

      if (!bestCandidate) {
        console.warn('Path generation halted: no valid candidates.');
        break;
      }

      currentAngle = Math.atan2(bestCandidate.z - currentPos.z, bestCandidate.x - currentPos.x);
      const slightlyAbove = bestCandidate.clone();
      slightlyAbove.y += 0.1; // float above ground
      path.push(slightlyAbove);
      visited.push(slightlyAbove);
    }

    return path;
  }

  applyTerrainHeights(path: Vector3[], getHeightAt: (x: number, z: number) => number, verticalOffset: number): Vector3[] {
    return path.map((p) => new Vector3(p.x, getHeightAt(p.x, p.z) + verticalOffset, p.z));
  }

  smoothPath(points: Vector3[], steps = 100): Vector3[] {
    const curve = Curve3.CreateCatmullRomSpline(points, steps, false);
    return curve.getPoints();
  }

  createPathMesh(name: string, path: Vector3[], width: number, segmentsAcross: number = 4, yOffset = 0): Mesh {
    const halfWidth = width / 2;
    const numCross = segmentsAcross + 1;

    const positions: number[] = [];
    const indices: number[] = [];
    const uvs: number[] = [];

    let totalLength = 0;
    const segmentLengths: number[] = [0];

    // Generate cross-section points from -halfWidth to +halfWidth
    const crossXs: number[] = [];
    for (let i = 0; i < numCross; i++) {
      crossXs.push(-halfWidth + (width * i) / segmentsAcross);
    }

    for (let i = 0; i < path.length; i++) {
      const curr = path[i];
      const prev = path[i - 1] ?? path[i];
      const next = path[i + 1] ?? path[i];
      const forward = next.subtract(prev).normalize();

      let up = Vector3.Up();
      if (Math.abs(Vector3.Dot(forward, up)) > 0.99) {
        up = new Vector3(1, 0, 0);
      }

      const right = Vector3.Cross(up, forward).normalize();
      const center = curr.add(Vector3.Up().scale(yOffset));

      for (let j = 0; j < crossXs.length; j++) {
        const offset = right.scale(crossXs[j]);
        const pos = center.add(offset);
        positions.push(pos.x, pos.y, pos.z);
        uvs.push(j / (numCross - 1), totalLength * 0.2); // Repeat every 5m
      }

      const segLen = i > 0 ? Vector3.Distance(curr, prev) : 0;
      totalLength += segLen;
      segmentLengths.push(totalLength);
    }

    const rowSize = numCross;

    for (let i = 0; i < path.length - 1; i++) {
      for (let j = 0; j < numCross - 1; j++) {
        const idx = i * rowSize + j;
        indices.push(idx, idx + rowSize, idx + 1, idx + 1, idx + rowSize, idx + rowSize + 1);
      }
    }

    const road = new Mesh(name, this.level.game.scene);
    const vdata = new VertexData();
    vdata.positions = positions;
    vdata.indices = indices;
    vdata.uvs = uvs;
    vdata.applyToMesh(road);
    road.convertToFlatShadedMesh();

    return road;
  }

  /**
   * Creates a mesh by extruding a 2D shape along the path.
   * The shape is defined in local space where:
   * - X = across the path (left/right)
   * - Y = up/down
   * - Z is ignored (will be built along the path)
   * @param name Mesh name
   * @param path Array of path points
   * @param shape 2D cross-section shape (array of Vector3, Z is ignored).
   *   If the shape has a `solidHalfCount` property (from shapePipe/shapeHalfpipe with thickness),
   *   solid annular end caps are generated automatically for a manifold mesh.
   *   If the shape has `closedShape` set to false, the ring loops won't be closed.
   * @param yOffset Vertical offset from path
   * @param solidHalfCount If > 0, creates a solid annular mesh with this many segments in the half-circle.
   * @param capEnds If true, generates simple end caps for non-annular shapes (default: false). Assumes convex shape.
   * @param closedShapeParam If true, connects last point to first (default: true). Overridden by shape.closedShape if set.
   */
  createPathMeshWithShape(
    name: string,
    path: Vector3[],
    shape: Vector3[],
    yOffset = 0,
    solidHalfCount = 0,
    capEnds = false,
    closedShapeParam = false
  ): Mesh {
    const closedShape = (shape as SolidShape).closedShape ?? closedShapeParam;

    // If solidHalfCount argument is not provided (0), check if the shape has it defined
    if (solidHalfCount === 0 && (shape as SolidShape).solidHalfCount) {
      solidHalfCount = (shape as SolidShape).solidHalfCount!;
    }
    if (shape.length < 3) {
      throw new Error('Shape must have at least 3 points');
    }

    const positions: number[] = [];
    const indices: number[] = [];
    const uvs: number[] = [];

    const numShapePoints = shape.length;

    // Calculate total path length for UV mapping
    let totalLength = 0;
    const segmentLengths: number[] = [0];
    for (let i = 1; i < path.length; i++) {
      totalLength += Vector3.Distance(path[i - 1], path[i]);
      segmentLengths.push(totalLength);
    }

    // Calculate UV offsets for each shape point (based on perimeter position)
    const shapeUVs: number[] = [];
    let perimeterLength = 0;
    for (let j = 0; j < numShapePoints; j++) {
      shapeUVs.push(perimeterLength);
      const nextJ = (j + 1) % numShapePoints;
      perimeterLength += Vector3.Distance(shape[j], shape[nextJ]);
    }
    // Normalize shape UVs to 0-1 range
    for (let j = 0; j < numShapePoints; j++) {
      shapeUVs[j] /= perimeterLength;
    }

    // Generate vertices for each path point
    for (let i = 0; i < path.length; i++) {
      const curr = path[i];
      const prev = path[i - 1] ?? path[i];
      const next = path[i + 1] ?? path[i];

      // Calculate tangent (forward direction along path)
      const tangent = next.subtract(prev).normalize();

      // Calculate local coordinate system
      let up = Vector3.Up();
      if (Math.abs(Vector3.Dot(tangent, up)) > 0.99) {
        up = new Vector3(1, 0, 0);
      }

      const right = Vector3.Cross(up, tangent).normalize();
      const localUp = Vector3.Cross(tangent, right).normalize();

      // Path center with yOffset
      const center = curr.add(Vector3.Up().scale(yOffset));

      // Add vertices for each shape point
      for (let j = 0; j < numShapePoints; j++) {
        const shapePoint = shape[j];
        // Transform shape point to world space
        // shape.x -> right direction, shape.y -> up direction
        const worldPos = center.add(right.scale(shapePoint.x)).add(localUp.scale(shapePoint.y));

        positions.push(worldPos.x, worldPos.y, worldPos.z);

        // UV: u = position along shape perimeter, v = position along path
        const u = shapeUVs[j];
        const v = segmentLengths[i] * 0.2; // Repeat every 5m along path
        uvs.push(u, v);
      }

      // Add UV seam vertices for closed solid rings:
      // Duplicate the first vertex of each ring with u=1.0 so the closing quad
      // doesn't stretch the texture from (N-1)/N back to 0.
      if (solidHalfCount > 0) {
        const v = segmentLengths[i] * 0.2;
        // Outer ring seam vertex (duplicate of shape[0] with u=1.0)
        const outerFirst = shape[0];
        const outerPos = center.add(right.scale(outerFirst.x)).add(localUp.scale(outerFirst.y));
        positions.push(outerPos.x, outerPos.y, outerPos.z);
        uvs.push(1.0, v);
        // Inner ring seam vertex (duplicate of shape[N] with u=1.0)
        const innerFirst = shape[solidHalfCount];
        const innerPos = center.add(right.scale(innerFirst.x)).add(localUp.scale(innerFirst.y));
        positions.push(innerPos.x, innerPos.y, innerPos.z);
        uvs.push(1.0, v);
      }
    }

    // Build indices (connect each path segment's shape to the next)
    if (solidHalfCount > 0) {
      // For solid annular shapes, generate TWO separate closed ring loops.
      // Winding order must produce normals pointing OUTWARD from the solid volume:
      // - Outer ring: normals point radially outward (away from tube center)
      // - Inner ring: normals point radially inward (toward the hollow center)
      const N = solidHalfCount;
      // Stride per path point: numShapePoints + 2 seam vertices (outer seam, inner seam)
      const stride = numShapePoints + 2;
      for (let i = 0; i < path.length - 1; i++) {
        const baseIdx = i * stride;
        const nextBaseIdx = (i + 1) * stride;

        // Loop count: N for closed shapes (wrap around), N-1 for open shapes
        const loopCount = closedShape ? N : N - 1;

        // Outer ring (indices 0..N-1)
        for (let j = 0; j < loopCount; j++) {
          // For closing quad (j=N-1), use seam vertex (u=1.0) instead of wrapping to 0.
          // For open shapes, j stops at N-2, so nextJ is just j+1.
          const nextJ = j + 1 < N ? j + 1 : numShapePoints; // seam vertex at offset numShapePoints
          indices.push(baseIdx + j, baseIdx + nextJ, nextBaseIdx + j);
          indices.push(baseIdx + nextJ, nextBaseIdx + nextJ, nextBaseIdx + j);
        }

        // Inner ring (indices N..2N-1)
        // Inner shape vertices are reversed, so same winding pattern = inward normals
        for (let j = 0; j < loopCount; j++) {
          const currJ = N + j;
          // For closing quad (j=N-1), use inner seam vertex (u=1.0) at offset numShapePoints+1
          // For open shapes, j stops at N-2, so nextJ is just N+j+1.
          const nextJ = j + 1 < N ? N + j + 1 : numShapePoints + 1;
          indices.push(baseIdx + currJ, baseIdx + nextJ, nextBaseIdx + currJ);
          indices.push(baseIdx + nextJ, nextBaseIdx + nextJ, nextBaseIdx + currJ);
        }

        // Check if we need to close the sides (for open shapes like halfpipe/U-profile)
        // This generates the "rims" connecting outer and inner shells along the path
        if (!closedShape) {
          // Left Side Rim: Connect outer[0] and inner[2N-1]
          const lO = baseIdx + 0;
          const lI = baseIdx + 2 * N - 1;
          const nlO = nextBaseIdx + 0;
          const nlI = nextBaseIdx + 2 * N - 1;
          // Winding: (Outer, NextOuter, Inner) gives Up normal for Left side
          indices.push(lO, nlO, lI);
          indices.push(nlO, nlI, lI);

          // Right Side Rim: Connect outer[N-1] and inner[N]
          const rO = baseIdx + N - 1;
          const rI = baseIdx + N;
          const nrO = nextBaseIdx + N - 1;
          const nrI = nextBaseIdx + N;
          // Winding: (Outer, Inner, NextOuter) gives Up normal for Right side (Clockwise)
          indices.push(rO, rI, nrO);
          indices.push(nrO, rI, nrI);
        }
      }
    } else {
      const edgeCount = closedShape ? numShapePoints : numShapePoints - 1;
      for (let i = 0; i < path.length - 1; i++) {
        const baseIdx = i * numShapePoints;
        const nextBaseIdx = (i + 1) * numShapePoints;

        for (let j = 0; j < edgeCount; j++) {
          const nextJ = (j + 1) % numShapePoints;

          const idx0 = baseIdx + j;
          const idx1 = baseIdx + nextJ;
          const idx2 = nextBaseIdx + j;
          const idx3 = nextBaseIdx + nextJ;

          // Two triangles per quad
          if (closedShape) {
            // Closed shape (Tube): Winding (idx0, idx2, idx1) gives outward facing normals (consistent with Ribbon)
            indices.push(idx0, idx2, idx1);
            indices.push(idx1, idx2, idx3);
          } else {
            // Open shape (Ribbon): Winding (idx0, idx2, idx1) gives upward facing normals
            indices.push(idx0, idx2, idx1);
            indices.push(idx1, idx2, idx3);
          }
        }
      }
    }

    // Generate annular end caps for solid shapes (manifold mesh)
    // Only if capEnds is explicitly requested (default false in Level.ts)
    if (solidHalfCount > 0 && path.length >= 2 && capEnds) {
      const N = solidHalfCount; // points per ring

      // Start cap (path index 0) - connects outer[j] to inner[j]
      // Outer indices: 0..N-1, Inner indices: N..2N-1
      // Inner ring is reversed, so inner[j] corresponds to outer[N-1-j]
      const capStride = numShapePoints + 2;
      const startBase = 0;
      const loopCount = closedShape ? N : N - 1;

      for (let j = 0; j < loopCount; j++) {
        const outerCurr = startBase + j;
        const outerNext = startBase + ((j + 1) % N); // For open shape, this is just j+1 (j < N-1)
        // Inner ring is in reverse, so the corresponding inner vertex for outer[j]
        // is at index N + (N-1-j), and for outer[j+1] is at N + (N-1-((j+1)%N))
        const innerCurr = startBase + N + (N - 1 - j);
        const innerNext = startBase + N + (N - 1 - ((j + 1) % N));

        // Two triangles forming a quad between outer edge j and inner edge j
        // Winding: facing backward along path (start cap)
        // Flip winding relative to loop: (Outer, Inner, NextOuter)
        indices.push(outerCurr, innerCurr, outerNext);
        indices.push(outerNext, innerCurr, innerNext);
      }

      // End cap (last path index) - same layout but facing forward
      const endBase = (path.length - 1) * capStride; // Use capStride for endBase calculation
      for (let j = 0; j < loopCount; j++) {
        const outerCurr = endBase + j;
        const outerNext = endBase + ((j + 1) % N);
        const innerCurr = endBase + N + (N - 1 - j);
        const innerNext = endBase + N + (N - 1 - ((j + 1) % N));

        // Winding: facing forward along path (end cap)
        indices.push(outerCurr, outerNext, innerCurr);
        indices.push(outerNext, innerNext, innerCurr);
      }
    } else if (capEnds && solidHalfCount === 0 && path.length >= 2) {
      // Generate simple caps for non-annular shapes (e.g. solid block cutter)
      // Assumes convex shape, uses triangle fan from center(0)

      // Start Cap (at path index 0) - facing backwards
      // Reverse winding: 0, 2, 1
      const startBase = 0;
      for (let j = 1; j < numShapePoints - 1; j++) {
        indices.push(startBase, startBase + j + 1, startBase + j);
      }

      // End Cap (at last index) - facing forward
      // Winding: 0, 1, 2
      const endBase = (path.length - 1) * numShapePoints;
      for (let j = 1; j < numShapePoints - 1; j++) {
        indices.push(endBase, endBase + j, endBase + j + 1);
      }
    }

    const mesh = new Mesh(name, this.level.game.scene);
    const vData = new VertexData();
    vData.positions = positions;
    vData.indices = indices;
    vData.uvs = uvs;

    vData.applyToMesh(mesh);

    // Flat shading duplicates vertices (unshares them), which breaks the manifold
    // property required by CSG2. For solid shapes, compute smooth normals instead.
    if (solidHalfCount > 0 || capEnds) {
      const normals: number[] = [];
      VertexData.ComputeNormals(positions, indices, normals);
      mesh.setVerticesData('normal', normals);
    } else {
      mesh.convertToFlatShadedMesh();
    }

    return mesh;
  }

  createPathMeshWithHeight(name: string, path: Vector3[], width: number, height: number, yOffset = 0): Mesh {
    // Deduplicate path to prevent zero-tangent issues
    const cleanPath: Vector3[] = [];
    if (path.length > 0) {
      cleanPath.push(path[0]);
      for (let i = 1; i < path.length; i++) {
        if (Vector3.Distance(path[i], cleanPath[cleanPath.length - 1]) > 0.01) {
          cleanPath.push(path[i]);
        }
      }
    }
    path = cleanPath;

    if (path.length < 2) {
      return new Mesh(name, this.level.game.scene);
    }

    const halfWidth = width / 2;

    const positions: number[] = [];
    const indices: number[] = [];
    const uvs: number[] = [];

    // Pre-calculate path points and frames
    const points: Vector3[] = [];
    const rights: Vector3[] = [];
    const segmentLengths: number[] = [0];
    let totalLength = 0;

    for (let i = 0; i < path.length; i++) {
      const curr = path[i];
      points.push(curr.add(Vector3.Up().scale(yOffset))); // Center point with Y offset

      // Calculate tangent
      const prev = path[i - 1] ?? path[i];
      const next = path[i + 1] ?? path[i];
      const tangent = next.subtract(prev).normalize();

      let up = Vector3.Up();
      if (Math.abs(Vector3.Dot(tangent, up)) > 0.99) {
        up = new Vector3(1, 0, 0);
      }
      const right = Vector3.Cross(up, tangent).normalize().scale(halfWidth);
      rights.push(right);

      if (i > 0) {
        const segLen = Vector3.Distance(path[i], path[i - 1]);
        totalLength += segLen;
        segmentLengths.push(totalLength);
      }
    }

    const uvScale = 0.2; // 1 unit texture per 5 world units

    // --- Generate Strip Vertices ---
    // We generate 4 independent strips to keep hard edges between faces
    // Top, Bottom, Left, Right

    // 1. Top Face Strip
    // Vertices: Left and Right for each point
    const topStartIndex = positions.length / 3;
    for (let i = 0; i < path.length; i++) {
      // Left
      const pL = points[i].subtract(rights[i]);
      // Right
      const pR = points[i].add(rights[i]);

      positions.push(pL.x, pL.y, pL.z); // Index: topStartIndex + 2*i
      positions.push(pR.x, pR.y, pR.z); // Index: topStartIndex + 2*i + 1

      const v = segmentLengths[i] * uvScale;
      uvs.push(0, v);
      uvs.push(1, v);
    }

    // 2. Bottom Face Strip
    const bottomStartIndex = positions.length / 3;
    for (let i = 0; i < path.length; i++) {
      // Left (Bottom side)
      const pL = points[i].subtract(rights[i]).subtract(Vector3.Up().scale(height));
      // Right (Bottom side)
      const pR = points[i].add(rights[i]).subtract(Vector3.Up().scale(height));

      positions.push(pL.x, pL.y, pL.z);
      positions.push(pR.x, pR.y, pR.z);

      const v = segmentLengths[i] * uvScale;
      uvs.push(0, v);
      uvs.push(1, v);
    }

    // 3. Left Face Strip
    const leftStartIndex = positions.length / 3;
    for (let i = 0; i < path.length; i++) {
      // Top Left
      const pTL = points[i].subtract(rights[i]);
      // Bottom Left
      const pBL = points[i].subtract(rights[i]).subtract(Vector3.Up().scale(height));

      positions.push(pTL.x, pTL.y, pTL.z);
      positions.push(pBL.x, pBL.y, pBL.z);

      const v = segmentLengths[i] * uvScale;
      const uHeight = height * uvScale;
      uvs.push(0, v);
      uvs.push(uHeight, v);
    }

    // 4. Right Face Strip
    const rightStartIndex = positions.length / 3;
    for (let i = 0; i < path.length; i++) {
      // Top Right
      const pTR = points[i].add(rights[i]);
      // Bottom Right
      const pBR = points[i].add(rights[i]).subtract(Vector3.Up().scale(height));

      positions.push(pTR.x, pTR.y, pTR.z);
      positions.push(pBR.x, pBR.y, pBR.z);

      const v = segmentLengths[i] * uvScale;
      const uHeight = height * uvScale;
      uvs.push(uHeight, v);
      uvs.push(0, v);
    }

    // --- Generate Indices ---

    // Top Face (SubMesh 0)

    // We need normals pointing UP.
    // Left x Forward = Up? No, Forward x Right = Up.
    // (pL(i+1) - pL(i)) x (pR(i) - pL(i)) -> Forward x Right -> Up.
    // So pL(i) -> pL(i+1) -> pR(i) is CCW.

    // Indices:
    // L(i), NL(i+1), NR(i+1)
    // L(i), NR(i+1), R(i)

    const topIndicesStart = indices.length;
    for (let i = 0; i < path.length - 1; i++) {
      const base = topStartIndex + 2 * i;
      const next = base + 2;

      const l = base;
      const r = base + 1;
      const nl = next;
      const nr = next + 1;

      // Tri 1: Left -> NextRight -> NextLeft (Reversed winding)
      indices.push(l, nr, nl);
      // Tri 2: Left -> Right -> NextRight (Reversed winding)
      indices.push(l, r, nr);
    }
    const topIndicesCount = indices.length - topIndicesStart;

    // Bottom Face
    // Needs Normal DOWN.
    // Vertices: L, R. Same structure.
    // Forward x Right = Up. So we want Right x Forward = Down.
    // (R(i)-L(i)) x (L(i+1)-L(i)) = Right x Forward = Down.
    // Tri: L(i), R(i), L(i+1)

    for (let i = 0; i < path.length - 1; i++) {
      const base = bottomStartIndex + 2 * i;
      const next = base + 2;

      const l = base;
      const r = base + 1;
      const nl = next;
      const nr = next + 1;

      // Tri 1: L -> R -> NL
      indices.push(l, r, nl);
      // Tri 2: R -> NR -> NL
      indices.push(r, nr, nl);
    }

    // Left Face
    // Vertices: TL, BL.
    // Normal: Left (-X locally).
    // Forward x Up = Left. Or Up x Forward? Up x Forward = Left (Y x Z = X?).
    // Left coordinate system usually.
    // Let's use the explicit points.
    // TopLeft(i), TopLeft(i+1), BotLeft(i+1), BotLeft(i).
    // Tangent is forward. Up is up. Right is right.
    // Left face normal is -Right.
    // (TL(i+1)-TL(i)) x (BL(i)-TL(i)) -> Forward x Down -> -Right.
    // So TL(i) -> TL(i+1) -> BL(i)

    for (let i = 0; i < path.length - 1; i++) {
      const base = leftStartIndex + 2 * i;
      const next = base + 2;

      const tl = base;
      const bl = base + 1;
      const ntl = next;
      const nbl = next + 1;

      // Tri 1: TL -> NTL -> NBL (Forward x Down -> -Right) Note: BL is next in list so index+1? Yes.
      // Wait, verts are TL, BL.
      // base = TL, base+1 = BL.

      indices.push(tl, ntl, nbl);
      indices.push(tl, nbl, bl);
    }

    // Right Face
    // Normal: Right.
    // Vertices: TR, BR.
    // (TR(i+1)-TR(i)) x (BR(i)-TR(i)) -> Forward x Down -> -Right (Wrong).
    // We want Right.
    // (BR(i)-TR(i)) x (TR(i+1)-TR(i)) -> Down x Forward -> Right? No.
    // Down x Forward = -Y x Z = -X (Left).
    // We want Right (+X).
    // Forward x Up = Right? No. Forward x Up = -Right (Z x Y = -X).
    // Wait, Babylon Left Handed. X right, Y up, Z forward.
    // X x Y = Z. Y x Z = X. Z x X = Y.
    // So Up (Y) x Forward (Z) = Right (X).
    // So (TL(i)-BL(i)) x (TL(i+1)-TL(i)) -> Up x Forward -> Right.
    // So BL -> TL -> NTL.

    for (let i = 0; i < path.length - 1; i++) {
      const base = rightStartIndex + 2 * i;
      const next = base + 2;

      const tr = base;
      const br = base + 1;
      const ntr = next;
      const nbr = next + 1;

      // we want BL -> TL -> NTL equivalent.
      // BR -> TR -> NTR
      indices.push(br, tr, ntr);
      indices.push(br, ntr, nbr);
    }

    // Caps (start/end) are single quads, so independent.
    const capIndicesStart = indices.length;
    // Start Cap
    if (path.length > 0) {
      const i = 0;
      const tl = points[i].subtract(rights[i]);
      const tr = points[i].add(rights[i]);
      const br = tr.subtract(Vector3.Up().scale(height));
      const bl = tl.subtract(Vector3.Up().scale(height));

      // Use simple non-shared vertices for caps, append to positions
      const start = positions.length / 3;
      const uWidth = width * uvScale;
      const vHeight = height * uvScale;

      positions.push(tr.x, tr.y, tr.z);
      positions.push(br.x, br.y, br.z);
      positions.push(bl.x, bl.y, bl.z);
      positions.push(tl.x, tl.y, tl.z);

      uvs.push(uWidth, 0);
      uvs.push(uWidth, vHeight);
      uvs.push(0, vHeight);
      uvs.push(0, 0);

      // Normal pointing back (-Tangent).
      // TR -> BR -> BL -> TL is CW?
      // (BR-TR) x (TL-TR) -> Down x Left -> Forward (Wrong).
      // (TL-TR) x (BR-TR) -> Left x Down -> Back. Correct.
      // TR -> TL -> BR
      indices.push(start, start + 3, start + 1);
      indices.push(start + 3, start + 2, start + 1);
    }

    // End Cap
    if (path.length > 0) {
      const i = path.length - 1;
      const tl = points[i].subtract(rights[i]);
      const tr = points[i].add(rights[i]);
      const br = tr.subtract(Vector3.Up().scale(height));
      const bl = tl.subtract(Vector3.Up().scale(height));

      const start = positions.length / 3;
      const uWidth = width * uvScale;
      const vHeight = height * uvScale;

      positions.push(tl.x, tl.y, tl.z);
      positions.push(bl.x, bl.y, bl.z);
      positions.push(br.x, br.y, br.z);
      positions.push(tr.x, tr.y, tr.z);

      uvs.push(0, 0);
      uvs.push(0, vHeight);
      uvs.push(uWidth, vHeight);
      uvs.push(uWidth, 0);

      // Normal pointing forward (Tangent).
      // TL -> BL -> BR -> TR.
      // (BL-TL) x (TR-TL) -> Down x Right -> Back (Wrong).
      // (TR-TL) x (BL-TL) -> Right x Down -> Forward. Correct.
      // TL -> TR -> BL
      indices.push(start, start + 3, start + 1);
      indices.push(start + 3, start + 2, start + 1);
    }

    const customMesh = new Mesh(name, this.level.game.scene);
    const vData = new VertexData();
    vData.positions = positions;
    vData.indices = indices;
    vData.uvs = uvs;

    // Compute normals - now they will be smooth along the strips!
    const normals: number[] = [];
    VertexData.ComputeNormals(positions, indices, normals);
    vData.normals = normals;

    vData.tangents = PathFactory.computeTangents(positions, normals, uvs, indices);

    vData.applyToMesh(customMesh);

    // SubMeshes
    // SubMesh 0: Top face indices
    // SubMesh 1: Rest (Bottom, Left, Right, Caps)

    const sideIndicesCount = indices.length - topIndicesCount;

    customMesh.subMeshes = [];
    new SubMesh(0, 0, positions.length / 3, 0, topIndicesCount, customMesh);
    new SubMesh(1, 0, positions.length / 3, topIndicesCount, sideIndicesCount, customMesh);

    return customMesh;
  }

  /**
   * Compute tangent vectors for a mesh given positions, normals, UVs and indices.
   * Tangents are required for proper Normal Map (bumpTexture) rendering.
   * Uses standard MikkTSpace-style algorithm.
   * @returns Float32Array of tangent data (x, y, z, w per vertex)
   */
  static computeTangents(positions: number[], normals: number[], uvs: number[], indices: number[]): number[] {
    const vertexCount = positions.length / 3;
    const tan1: Vector3[] = [];
    const tan2: Vector3[] = [];

    for (let i = 0; i < vertexCount; i++) {
      tan1.push(Vector3.Zero());
      tan2.push(Vector3.Zero());
    }

    // Iterate through triangles
    for (let i = 0; i < indices.length; i += 3) {
      const i1 = indices[i];
      const i2 = indices[i + 1];
      const i3 = indices[i + 2];

      const v1 = new Vector3(positions[i1 * 3], positions[i1 * 3 + 1], positions[i1 * 3 + 2]);
      const v2 = new Vector3(positions[i2 * 3], positions[i2 * 3 + 1], positions[i2 * 3 + 2]);
      const v3 = new Vector3(positions[i3 * 3], positions[i3 * 3 + 1], positions[i3 * 3 + 2]);

      const w1 = { u: uvs[i1 * 2], v: uvs[i1 * 2 + 1] };
      const w2 = { u: uvs[i2 * 2], v: uvs[i2 * 2 + 1] };
      const w3 = { u: uvs[i3 * 2], v: uvs[i3 * 2 + 1] };

      const x1 = v2.x - v1.x;
      const x2 = v3.x - v1.x;
      const y1 = v2.y - v1.y;
      const y2 = v3.y - v1.y;
      const z1 = v2.z - v1.z;
      const z2 = v3.z - v1.z;

      const s1 = w2.u - w1.u;
      const s2 = w3.u - w1.u;
      const t1 = w2.v - w1.v;
      const t2 = w3.v - w1.v;

      const denom = s1 * t2 - s2 * t1;
      const r = Math.abs(denom) > 0.000001 ? 1.0 / denom : 0.0;

      const sdir = new Vector3((t2 * x1 - t1 * x2) * r, (t2 * y1 - t1 * y2) * r, (t2 * z1 - t1 * z2) * r);

      const tdir = new Vector3((s1 * x2 - s2 * x1) * r, (s1 * y2 - s2 * y1) * r, (s1 * z2 - s2 * z1) * r);

      tan1[i1] = tan1[i1].add(sdir);
      tan1[i2] = tan1[i2].add(sdir);
      tan1[i3] = tan1[i3].add(sdir);

      tan2[i1] = tan2[i1].add(tdir);
      tan2[i2] = tan2[i2].add(tdir);
      tan2[i3] = tan2[i3].add(tdir);
    }

    // Final tangent calculation with handedness
    const tangents: number[] = [];
    for (let i = 0; i < vertexCount; i++) {
      const n = new Vector3(normals[i * 3], normals[i * 3 + 1], normals[i * 3 + 2]);
      const t = tan1[i];

      // Check if tangent is valid (not zero-length)
      const tLength = t.length();
      let tangent: Vector3;

      if (tLength < 0.0001) {
        // No valid tangent from UV deltas - compute a fallback tangent
        // Use a direction perpendicular to the normal
        let fallback = Vector3.Cross(n, Vector3.Up());
        if (fallback.length() < 0.0001) {
          fallback = Vector3.Cross(n, Vector3.Right());
        }
        tangent = fallback.normalize();
      } else {
        // Gram-Schmidt orthogonalize
        tangent = t.subtract(n.scale(Vector3.Dot(n, t)));
        const orthogLength = tangent.length();
        if (orthogLength < 0.0001) {
          // Fallback if orthogonalization failed
          let fallback = Vector3.Cross(n, Vector3.Up());
          if (fallback.length() < 0.0001) {
            fallback = Vector3.Cross(n, Vector3.Right());
          }
          tangent = fallback.normalize();
        } else {
          tangent = tangent.normalize();
        }
      }

      // Handedness (w component)
      const w = Vector3.Dot(Vector3.Cross(n, t), tan2[i]) < 0.0 ? -1.0 : 1.0;

      tangents.push(tangent.x, tangent.y, tangent.z, w);
    }

    return tangents;
  }

  static spiralPath(
    start: Vector3,
    destination: Vector3,
    startRadius: number,
    destinationRadius: number,
    turns: number,
    controlPointCount: number,
    curvePointCount: number
  ): Vector3[] {
    if (controlPointCount < 2) {
      return [start.clone(), destination.clone()];
    }

    const controlPoints: Vector3[] = [];

    // Calculate the center axis of the spiral (line from start to destination)
    const axis = destination.subtract(start);
    const axisLength = axis.length();
    const axisDirection = axis.normalize();

    // Calculate perpendicular vectors for the spiral rotation
    // We'll rotate in the XZ plane if the axis is mostly vertical,
    // otherwise we'll create a proper local coordinate system
    let right = Vector3.Cross(axisDirection, Vector3.Up());
    if (right.length() < 0.001) {
      // Axis is vertical, use a default perpendicular
      right = Vector3.Cross(axisDirection, Vector3.Right());
    }
    right = right.normalize();
    const forward = Vector3.Cross(axisDirection, right).normalize();

    // Generate spiral control points
    for (let i = 0; i < controlPointCount; i++) {
      const t = i / (controlPointCount - 1); // 0 to 1

      // Interpolate along the axis from start to destination
      const centerPoint = Vector3.Lerp(start, destination, t);

      // Interpolate radius from startRadius to destinationRadius
      const currentRadius = startRadius + (destinationRadius - startRadius) * t;

      // Calculate angle based on number of turns
      const angle = t * turns * Math.PI * 2; // Full rotations

      // Calculate offset from center using the perpendicular vectors
      const offsetX = Math.cos(angle) * currentRadius;
      const offsetZ = Math.sin(angle) * currentRadius;

      // Apply offset in the local coordinate system
      const offset = right.scale(offsetX).add(forward.scale(offsetZ));
      const spiralPoint = centerPoint.add(offset);

      controlPoints.push(spiralPoint);
    }

    // Create a smooth CatmullRomSpline from the control points and curve point count
    const curve = Curve3.CreateCatmullRomSpline(controlPoints, curvePointCount, false);
    const points = curve.getPoints();

    return points;
  }
}
